import { useState } from 'react';
import { ErroreApi } from '../api/cliente.js';
import { peso } from '../api/documenti.js';
import {
  type AttivitaTrattativa,
  indirizzoAllegato,
  useAllegaFileTrattativa,
  useOpzioniTrattative,
  useSalvaAttivita,
} from '../api/trattative.js';
import { Modale } from '../componenti/Modale.js';
import { ZonaFile } from '../componenti/ZonaFile.js';
import { giornoLocale } from '../giorni.js';
import { t } from '../i18n/lingua.js';

/**
 * Un'attività della trattativa: una telefonata, una e-mail, un incontro.
 *
 * Data e ora stanno in due campi come nel v2 — è così che si scrivono, «il
 * 24 alle dieci» — e il tipo si sceglie da pastiglie invece che da una
 * tendina: sono otto, e con le pastiglie si vede subito quale si è preso.
 *
 * L'allegato si carica solo su un'attività che esiste già: prima si salva,
 * poi si attacca il file.
 */
export function ModaleAttivita({
  trattativaId,
  attivita,
  onChiudi,
}: {
  trattativaId: number;
  /** Nulla per un'attività nuova. */
  attivita: AttivitaTrattativa | null;
  onChiudi: () => void;
}) {
  const salva = useSalvaAttivita(trattativaId);
  const allega = useAllegaFileTrattativa(trattativaId);
  const opzioni = useOpzioniTrattative();

  const quando = attivita ? new Date(attivita.activityDate) : new Date();
  const oreMinuti = `${String(quando.getHours()).padStart(2, '0')}:${String(
    quando.getMinutes(),
  ).padStart(2, '0')}`;

  const [campi, setCampi] = useState({
    activityType: attivita?.activityType ?? 'phone_call',
    // il giorno e l'ora nello stesso fuso, quello di chi scrive: il giorno
    // di Greenwich con l'ora italiana portava le 00:30 al giorno prima
    giorno: giornoLocale(quando),
    ora: oreMinuti,
    description: attivita?.description ?? '',
    extendedDescription: attivita?.extendedDescription ?? '',
  });

  const cambia = (chiave: keyof typeof campi, valore: string) =>
    setCampi((c) => ({ ...c, [chiave]: valore }));

  const errore =
    salva.error instanceof ErroreApi
      ? salva.error.message
      : allega.error instanceof ErroreApi
        ? allega.error.message
        : null;

  const conferma = () =>
    salva.mutate(
      {
        id: attivita?.id ?? null,
        activityType: campi.activityType,
        // data e ora tornano insieme: il servizio vuole un istante, non due
        // campi da rimettere insieme lui
        activityDate: new Date(`${campi.giorno}T${campi.ora || '00:00'}:00`).toISOString(),
        description: campi.description.trim(),
        extendedDescription: campi.extendedDescription.trim() || null,
      },
      { onSuccess: onChiudi },
    );

  const pronto = campi.description.trim() !== '' && campi.giorno !== '';

  return (
    <Modale
      titolo={attivita ? t('Modifica l’attività') : t('Registra un’attività')}
      larga
      inCorso={salva.isPending}
      onChiudi={onChiudi}
      azioni={
        <>
          <button className="btn" onClick={onChiudi} disabled={salva.isPending}>
            {t('Annulla')}
          </button>
          <button
            className="btn btn-primary"
            onClick={conferma}
            disabled={!pronto || salva.isPending}
          >
            {salva.isPending ? 'Salvo…' : 'Salva'}
          </button>
        </>
      }
    >
      {errore && (
        <div className="avviso" role="alert" style={{ marginBottom: 12 }}>
          <span className="avviso-segno">▲</span>
          <span>{errore}</span>
        </div>
      )}

      <div className="field span-2" style={{ marginBottom: 14 }}>
        <label>{t('Tipo di attività')}</label>
        <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
          {(opzioni.data?.activityTypes ?? []).map((tipo) => (
            <button
              key={tipo.value}
              className={campi.activityType === tipo.value ? 'chip chip-set' : 'chip'}
              onClick={() => cambia('activityType', tipo.value)}
            >
              <span aria-hidden="true" style={{ marginRight: 4 }}>
                {tipo.segno}
              </span>
              {tipo.it}
            </button>
          ))}
        </div>
      </div>

      <div className="fields">
        <div className="field">
          <label htmlFor="at-giorno">
            {t('Data')}<span className="req">*</span>
          </label>
          <input
            id="at-giorno"
            className="input"
            type="date"
            value={campi.giorno}
            onChange={(e) => cambia('giorno', e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="at-ora">{t('Ora')}</label>
          <input
            id="at-ora"
            className="input"
            type="time"
            value={campi.ora}
            onChange={(e) => cambia('ora', e.target.value)}
          />
        </div>

        <div className="field span-2">
          <label htmlFor="at-descrizione">
            {t('Descrizione')}<span className="req">*</span>
          </label>
          <input
            id="at-descrizione"
            className="input"
            placeholder={t('Inviata e-mail scheda tecnica prodotti')}
            value={campi.description}
            autoFocus
            onChange={(e) => cambia('description', e.target.value)}
          />
        </div>

        <div className="field span-2">
          <label htmlFor="at-estesa">{t('Descrizione estesa')}</label>
          <textarea
            id="at-estesa"
            className="input"
            rows={3}
            value={campi.extendedDescription}
            onChange={(e) => cambia('extendedDescription', e.target.value)}
          />
        </div>
      </div>

      {/* il file si attacca a un'attività che esiste: prima si salva */}
      {attivita ? (
        <div style={{ marginTop: 14 }}>
          <label className="etichetta-sezione">{t('Allegati')}</label>
          {attivita.attachments.length > 0 && (
            <ul className="doclist" style={{ marginBottom: 8 }}>
              {attivita.attachments.map((f) => (
                <li key={f.id}>
                  <a
                    className="cell-link"
                    href={indirizzoAllegato(trattativaId, 'activity', f.id)}
                    download={f.fileName}
                  >
                    {f.fileName}
                  </a>
                  <span className="dim">{peso(f.size)}</span>
                </li>
              ))}
            </ul>
          )}
          <ZonaFile
            file={null}
            massimo={opzioni.data?.maxUploadBytes}
            onFile={(file) =>
              file && allega.mutate({ tipo: 'activities', id: attivita.id, file })
            }
          />
          {allega.isPending && (
            <p className="dim" style={{ fontSize: 12, marginBottom: 0 }}>
              {t('Sto caricando…')}
            </p>
          )}
        </div>
      ) : (
        <p className="dim" style={{ fontSize: 12, marginTop: 14, marginBottom: 0 }}>
          {t('Gli allegati si aggiungono dopo aver salvato: il file ha bisogno di un\'attività a cui attaccarsi.')}
        </p>
      )}
    </Modale>
  );
}
